import { Plus, LayoutTemplate, Palette, Rows3 } from 'lucide-react';
import { useFontCatalog } from '../../hooks/useFontCatalog';
import { THEME_PRESETS } from '../../lib/siteTheme';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

export function SettingsEditor({ settings, setSettings }) {
  const updateSettings = (path, value) => {
    setSettings((prev) => {
      const next = { ...prev };
      let ref = next;
      for (let i = 0; i < path.length - 1; i += 1) {
        ref[path[i]] = { ...(ref[path[i]] || {}) };
        ref = ref[path[i]];
      }
      ref[path[path.length - 1]] = value;
      return next;
    });
  };

  const updateMenuItems = (items) => {
    updateSettings(['header', 'menu'], items);
  };

  const updateFooterSections = (sections) => {
    updateSettings(['footer', 'sections'], sections);
  };

  const updateFooterSocials = (socials) => {
    updateSettings(['footer', 'socials'], socials);
  };

  return (
    <div className="space-y-8">
      <EditorSection
        icon={LayoutTemplate}
        title="Header"
        description="Logo, main CTA and primary navigation shown on every page."
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input value={settings.header?.logo_text || ''} onChange={(e) => updateSettings(['header', 'logo_text'], e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="Logo text" />
          <Input value={settings.header?.logo_tagline || ''} onChange={(e) => updateSettings(['header', 'logo_tagline'], e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="Logo tagline" />
          <Input value={settings.header?.cta_href || ''} onChange={(e) => updateSettings(['header', 'cta_href'], e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="CTA link" />
          <Input value={settings.header?.cta_label || ''} onChange={(e) => updateSettings(['header', 'cta_label'], e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="CTA label (RU)" />
          <Input value={settings.header?.cta_label_en || ''} onChange={(e) => updateSettings(['header', 'cta_label_en'], e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="CTA label (EN)" />
        </div>
        <MenuEditor items={settings.header?.menu || []} onChange={updateMenuItems} />
      </EditorSection>

      <EditorSection
        icon={Rows3}
        title="Footer"
        description="Contact details, footer sections and social links used across the site."
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input value={settings.footer?.address || ''} onChange={(e) => updateSettings(['footer', 'address'], e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="Address (RU)" />
          <Input value={settings.footer?.address_en || ''} onChange={(e) => updateSettings(['footer', 'address_en'], e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="Address (EN)" />
          <Input value={settings.footer?.email || ''} onChange={(e) => updateSettings(['footer', 'email'], e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="Email" />
          <Input value={settings.footer?.phone || ''} onChange={(e) => updateSettings(['footer', 'phone'], e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="Phone" />
        </div>

        <FooterSectionsEditor sections={settings.footer?.sections || []} onChange={updateFooterSections} />
        <FooterSocialsEditor socials={settings.footer?.socials || []} onChange={updateFooterSocials} />
      </EditorSection>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard title="Menu Items" value={(settings.header?.menu || []).length} description="Top-level navigation links" />
        <SummaryCard title="Footer Sections" value={(settings.footer?.sections || []).length} description="Structured link groups" />
        <SummaryCard title="Social Links" value={(settings.footer?.socials || []).length} description="External community channels" />
      </div>
    </div>
  );
}

function EditorSection({ icon: Icon, title, description, children }) {
  return (
    <section className="space-y-4 rounded-3xl border border-white/10 bg-white/[0.03] p-5 md:p-6">
      <div className="flex items-start gap-3">
        <div className="mt-1 rounded-2xl bg-white/10 p-2 text-white">
          <Icon className="h-4 w-4" />
        </div>
        <div className="space-y-1">
          <h3 className="text-white font-bold text-lg">{title}</h3>
          <p className="text-sm text-white/60">{description}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function SummaryCard({ title, value, description }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
      <div className="text-sm text-white/60">{title}</div>
      <div className="mt-2 text-2xl font-black text-white">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-widest text-white/40">{description}</div>
    </div>
  );
}

function MenuEditor({ items, onChange }) {
  const updateItem = (index, field, value) => {
    const next = [...items];
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  };

  const addItem = () => {
    onChange([...(items || []), { label: '', label_en: '', href: '', children: [] }]);
  };

  const removeItem = (index) => {
    onChange(items.filter((_, idx) => idx !== index));
  };

  const addChild = (index) => {
    const next = [...items];
    const children = next[index].children || [];
    next[index] = { ...next[index], children: [...children, { label: '', label_en: '', href: '' }] };
    onChange(next);
  };

  const updateChild = (parentIndex, childIndex, field, value) => {
    const next = [...items];
    const children = [...(next[parentIndex].children || [])];
    children[childIndex] = { ...children[childIndex], [field]: value };
    next[parentIndex] = { ...next[parentIndex], children };
    onChange(next);
  };

  const removeChild = (parentIndex, childIndex) => {
    const next = [...items];
    const children = (next[parentIndex].children || []).filter((_, idx) => idx !== childIndex);
    next[parentIndex] = { ...next[parentIndex], children };
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-white font-bold">Menu</h4>
        <Button onClick={addItem} className="bg-white/10 hover:bg-white/20 text-white rounded-lg">
          <Plus className="w-4 h-4 mr-2" />
          Add item
        </Button>
      </div>

      <div className="space-y-4">
        {items.map((item, index) => (
          <div key={`${item.href}-${index}`} className="glass rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input value={item.label || ''} onChange={(e) => updateItem(index, 'label', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="Label (RU)" />
              <Input value={item.label_en || ''} onChange={(e) => updateItem(index, 'label_en', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="Label (EN)" />
              <Input value={item.href || ''} onChange={(e) => updateItem(index, 'href', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="/path or https://" />
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => addChild(index)} className="bg-white/10 hover:bg-white/20 text-white rounded-lg">Add child</Button>
              <Button onClick={() => removeItem(index)} className="bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg">Remove</Button>
            </div>
            {(item.children || []).length > 0 && (
              <div className="pl-4 space-y-3">
                {item.children.map((child, childIndex) => (
                  <div key={`${child.href}-${childIndex}`} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Input value={child.label || ''} onChange={(e) => updateChild(index, childIndex, 'label', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="Child label (RU)" />
                    <Input value={child.label_en || ''} onChange={(e) => updateChild(index, childIndex, 'label_en', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="Child label (EN)" />
                    <div className="flex gap-2">
                      <Input value={child.href || ''} onChange={(e) => updateChild(index, childIndex, 'href', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="/child" />
                      <Button onClick={() => removeChild(index, childIndex)} className="bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg">Remove</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FooterSectionsEditor({ sections, onChange }) {
  const addSection = () => {
    onChange([...(sections || []), { title: '', title_en: '', links: [] }]);
  };

  const updateSection = (index, field, value) => {
    const next = [...sections];
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  };

  const removeSection = (index) => {
    onChange(sections.filter((_, idx) => idx !== index));
  };

  const addLink = (index) => {
    const next = [...sections];
    const links = next[index].links || [];
    next[index] = { ...next[index], links: [...links, { label: '', label_en: '', href: '' }] };
    onChange(next);
  };

  const updateLink = (sectionIndex, linkIndex, field, value) => {
    const next = [...sections];
    const links = [...(next[sectionIndex].links || [])];
    links[linkIndex] = { ...links[linkIndex], [field]: value };
    next[sectionIndex] = { ...next[sectionIndex], links };
    onChange(next);
  };

  const removeLink = (sectionIndex, linkIndex) => {
    const next = [...sections];
    const links = (next[sectionIndex].links || []).filter((_, idx) => idx !== linkIndex);
    next[sectionIndex] = { ...next[sectionIndex], links };
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-white font-bold">Footer Sections</h4>
        <Button onClick={addSection} className="bg-white/10 hover:bg-white/20 text-white rounded-lg">
          <Plus className="w-4 h-4 mr-2" />
          Add section
        </Button>
      </div>
      <div className="space-y-4">
        {sections.map((section, index) => (
          <div key={`${section.title}-${index}`} className="glass rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input value={section.title || ''} onChange={(e) => updateSection(index, 'title', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="Title (RU)" />
              <Input value={section.title_en || ''} onChange={(e) => updateSection(index, 'title_en', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="Title (EN)" />
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => addLink(index)} className="bg-white/10 hover:bg-white/20 text-white rounded-lg">Add link</Button>
              <Button onClick={() => removeSection(index)} className="bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg">Remove section</Button>
            </div>
            {(section.links || []).map((link, linkIndex) => (
              <div key={`${link.href}-${linkIndex}`} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input value={link.label || ''} onChange={(e) => updateLink(index, linkIndex, 'label', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="Label (RU)" />
                <Input value={link.label_en || ''} onChange={(e) => updateLink(index, linkIndex, 'label_en', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="Label (EN)" />
                <div className="flex gap-2">
                  <Input value={link.href || ''} onChange={(e) => updateLink(index, linkIndex, 'href', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="/path or https://" />
                  <Button onClick={() => removeLink(index, linkIndex)} className="bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg">Remove</Button>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function FooterSocialsEditor({ socials, onChange }) {
  const addSocial = () => {
    onChange([...(socials || []), { label: '', href: '' }]);
  };

  const updateSocial = (index, field, value) => {
    const next = [...socials];
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  };

  const removeSocial = (index) => {
    onChange(socials.filter((_, idx) => idx !== index));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-white font-bold">Social Links</h4>
        <Button onClick={addSocial} className="bg-white/10 hover:bg-white/20 text-white rounded-lg">
          <Plus className="w-4 h-4 mr-2" />
          Add social
        </Button>
      </div>
      <div className="space-y-3">
        {socials.map((social, index) => (
          <div key={`${social.href}-${index}`} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input value={social.label || ''} onChange={(e) => updateSocial(index, 'label', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="Label" />
            <Input value={social.href || ''} onChange={(e) => updateSocial(index, 'href', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="https://" />
            <Button onClick={() => removeSocial(index)} className="bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg">Remove</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ThemeEditor({ settings, setSettings }) {
  const theme = settings.theme || {};
  const { families } = useFontCatalog();
  const fontOptions = families.length ? families : [
    'Roboto', 'Open Sans', 'Montserrat', 'Inter', 'Lato', 'Poppins', 'Nunito', 'Source Sans 3', 'Fira Sans', 'Rubik', 'Arial',
  ];

  const updateTheme = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      theme: {
        ...prev.theme,
        [field]: value,
      },
    }));
  };

  const ColorField = ({ label, field, value }) => (
    <div className="flex items-center gap-3 bg-zinc-900/40 border border-white/10 rounded-xl px-3 py-2">
      <input type="color" value={value || '#000000'} onChange={(e) => updateTheme(field, e.target.value)} className="h-9 w-9 rounded-lg border border-white/20 bg-transparent" />
      <div className="flex-1">
        <Label className="text-white/70 text-xs">{label}</Label>
        <Input value={value || ''} onChange={(e) => updateTheme(field, e.target.value)} className="bg-transparent border-none text-white px-0 h-8" placeholder="#000000" />
      </div>
    </div>
  );

  const previewHeading = theme.heading_font || 'Arial';
  const previewBody = theme.body_font || 'Arial';

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 md:p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="mt-1 rounded-2xl bg-white/10 p-2 text-white">
            <Palette className="h-4 w-4" />
          </div>
          <div className="space-y-1">
            <h3 className="text-white font-bold text-lg">Theme Controls</h3>
            <p className="text-sm text-white/60">Choose palette, fonts and scaling tokens used across the public site.</p>
          </div>
        </div>
      <div className="flex flex-wrap gap-2">
        {THEME_PRESETS.map((preset) => (
          <Button
            key={preset.name}
            onClick={() => {
              updateTheme('primary', preset.primary);
              updateTheme('secondary', preset.secondary);
              updateTheme('accent', preset.accent);
              updateTheme('background', preset.background);
              updateTheme('foreground', preset.foreground);
            }}
            className="bg-white/10 hover:bg-white/20 text-white rounded-xl"
          >
            <span className="inline-flex items-center gap-2">
              <span className="h-3 w-3 rounded-full" style={{ background: preset.primary }} />
              {preset.name}
            </span>
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ColorField label="Primary" field="primary" value={theme.primary} />
        <ColorField label="Secondary" field="secondary" value={theme.secondary} />
        <ColorField label="Accent" field="accent" value={theme.accent} />
        <ColorField label="Background" field="background" value={theme.background} />
        <ColorField label="Foreground" field="foreground" value={theme.foreground} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-white/70 text-xs">Heading font</Label>
          <select value={theme.heading_font || ''} onChange={(e) => updateTheme('heading_font', e.target.value)} className="bg-zinc-900/50 border border-white/10 text-white rounded-lg px-4 py-2 w-full">
            <option value="">Select font</option>
            {fontOptions.map((font) => (
              <option key={font} value={font}>{font}</option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <Label className="text-white/70 text-xs">Body font</Label>
          <select value={theme.body_font || ''} onChange={(e) => updateTheme('body_font', e.target.value)} className="bg-zinc-900/50 border border-white/10 text-white rounded-lg px-4 py-2 w-full">
            <option value="">Select font</option>
            {fontOptions.map((font) => (
              <option key={font} value={font}>{font}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-white/70 text-xs">Site zoom</Label>
          <Input
            type="number"
            min="0.8"
            max="1.25"
            step="0.05"
            value={theme.site_zoom || 1}
            onChange={(e) => updateTheme('site_zoom', e.target.value)}
            className="bg-zinc-900/50 border-white/10 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white/70 text-xs">Radius scale</Label>
          <Input
            type="number"
            min="0.75"
            max="1.5"
            step="0.05"
            value={theme.radius_scale || 1}
            onChange={(e) => updateTheme('radius_scale', e.target.value)}
            className="bg-zinc-900/50 border-white/10 text-white"
          />
        </div>
      </div>
      </div>
      <div className="rounded-3xl border border-white/10 p-6 md:p-8 space-y-4" style={{ background: theme.background || '#050505', color: theme.foreground || '#FFFFFF' }}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-2xl md:text-3xl font-black" style={{ fontFamily: previewHeading }}>Theme Preview</div>
            <div className="text-sm md:text-base opacity-80" style={{ fontFamily: previewBody }}>
              Headings use the selected heading font, body text uses the selected body font.
            </div>
          </div>
          <div className="flex gap-2">
            <span className="h-8 w-8 rounded-full border border-white/20" style={{ background: theme.primary || '#E11D2E' }} />
            <span className="h-8 w-8 rounded-full border border-white/20" style={{ background: theme.secondary || '#0A0A0A' }} />
            <span className="h-8 w-8 rounded-full border border-white/20" style={{ background: theme.accent || '#FFFFFF' }} />
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <button type="button" className="px-5 py-3 rounded-full font-semibold text-white" style={{ background: theme.primary || '#E11D2E' }}>Primary Action</button>
          <button type="button" className="px-5 py-3 rounded-full font-semibold border" style={{ borderColor: theme.accent || '#FFFFFF', color: theme.accent || '#FFFFFF' }}>Secondary Action</button>
        </div>
        <p className="text-sm opacity-70" style={{ fontFamily: previewBody }}>
          Use this preview to quickly see how the palette and typography feel together before saving.
        </p>
      </div>
    </div>
  );
}

export function DesignControlsEditor({ settings, setSettings }) {
  const theme = settings.theme || {};

  const updateTheme = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      theme: {
        ...prev.theme,
        [field]: value,
      },
    }));
  };

  const RangeField = ({ label, field, value, min = 0.8, max = 1.6, step = 0.05 }) => (
    <div className="space-y-2 rounded-2xl border border-white/10 bg-black/20 p-4">
      <Label className="text-white/70 text-xs">{label}</Label>
      <Input type="number" min={min} max={max} step={step} value={value || 1} onChange={(e) => updateTheme(field, e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
    </div>
  );

  const ColorField = ({ label, field, value, fallback = '#FFFFFF' }) => (
    <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/20 p-4">
      <input type="color" value={typeof value === 'string' && value.startsWith('#') ? value : fallback} onChange={(e) => updateTheme(field, e.target.value)} className="h-10 w-10 rounded-lg border border-white/20 bg-transparent" />
      <div className="flex-1 space-y-1">
        <Label className="text-white/70 text-xs">{label}</Label>
        <Input value={value || ''} onChange={(e) => updateTheme(field, e.target.value)} className="bg-transparent border-white/10 text-white" placeholder={fallback} />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <EditorSection
        icon={Palette}
        title="Quick Design Controls"
        description="Fast access to the main visual parameters of the public site."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <RangeField label="Hero title scale" field="hero_title_size" value={theme.hero_title_size} />
          <RangeField label="Section title scale" field="section_title_size" value={theme.section_title_size} />
          <RangeField label="Body text scale" field="body_text_size" value={theme.body_text_size} />
          <RangeField label="Small text scale" field="small_text_size" value={theme.small_text_size} />
          <RangeField label="Button text size" field="button_text_size" value={theme.button_text_size} />
          <RangeField label="Button radius" field="button_radius" value={theme.button_radius} min={0.5} max={2} />
          <RangeField label="Form text size" field="form_text_size" value={theme.form_text_size} />
          <RangeField label="Form radius" field="form_radius" value={theme.form_radius} min={0.5} max={2} />
        </div>
      </EditorSection>

      <EditorSection
        icon={Palette}
        title="Quick Colors"
        description="Headings, body text, buttons and form field appearance."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <ColorField label="Heading color" field="heading_color" value={theme.heading_color} />
          <ColorField label="Body text color" field="body_color" value={theme.body_color} />
          <ColorField label="Muted text color" field="muted_text_color" value={theme.muted_text_color} />
          <ColorField label="Button background" field="button_bg" value={theme.button_bg} fallback="#E11D2E" />
          <ColorField label="Button text" field="button_text" value={theme.button_text} />
          <ColorField label="Form text" field="form_text" value={theme.form_text} />
          <ColorField label="Form border" field="form_border" value={theme.form_border} />
        </div>
        <div className="space-y-2 rounded-2xl border border-white/10 bg-black/20 p-4">
          <Label className="text-white/70 text-xs">Form background</Label>
          <Input value={theme.form_bg || ''} onChange={(e) => updateTheme('form_bg', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="rgba(24,24,27,0.5) or #111111" />
        </div>
      </EditorSection>

      <div className="rounded-3xl border border-white/10 p-6 md:p-8 space-y-5 bg-black/20">
        <div className="space-y-2">
          <div className="font-heading font-black" style={{ fontSize: `calc(3rem * ${theme.hero_title_size || 1})`, color: theme.heading_color || '#FFFFFF', lineHeight: 0.95 }}>
            Hero Heading Preview
          </div>
          <div className="font-heading font-bold" style={{ fontSize: `calc(2rem * ${theme.section_title_size || 1})`, color: theme.heading_color || '#FFFFFF' }}>
            Section Heading Preview
          </div>
          <p style={{ fontSize: `calc(1rem * ${theme.body_text_size || 1})`, color: theme.body_color || 'rgba(255,255,255,0.78)' }}>
            Body copy preview for paragraphs, cards and detail pages.
          </p>
          <p style={{ fontSize: `calc(0.875rem * ${theme.small_text_size || 1})`, color: theme.muted_text_color || 'rgba(255,255,255,0.62)' }}>
            Small and muted supporting text preview.
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <button type="button" className="px-5 py-3 font-semibold" style={{ background: theme.button_bg || '#E11D2E', color: theme.button_text || '#FFFFFF', borderRadius: `${theme.button_radius || 1}rem`, fontSize: `calc(0.95rem * ${theme.button_text_size || 1})` }}>
            Primary Button
          </button>
          <input readOnly value="Form field preview" className="px-4 py-3 border w-full md:max-w-sm" style={{ background: theme.form_bg || 'rgba(24,24,27,0.5)', color: theme.form_text || '#FFFFFF', borderColor: theme.form_border || 'rgba(255,255,255,0.1)', borderRadius: `${theme.form_radius || 1}rem`, fontSize: `calc(0.95rem * ${theme.form_text_size || 1})` }} />
        </div>
      </div>
    </div>
  );
}
